import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { MessageSquare, Reply, Trash2, CheckCircle } from "lucide-react";
import ConfirmModal from "./ConfirmModal";
import { User, displayName, initials, avatarColor, loadUsers } from "./user-display";

interface Comment {
    id: string;
    targetType: string;
    targetId: string;
    parentCommentId: string | null;
    authorId: string;
    body: string;
    isAnswered: boolean;
    answeredBy: string | null;
    answeredAt: string | null;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string | null;
}

interface Props {
    pageId: string;
    isAdmin: boolean;
    currentUserId: string | null;
}

export default function CommentThread({ pageId, isAdmin, currentUserId }: Props) {
    const canDelete = (authorId: string) => isAdmin || authorId === currentUserId;
    const [comments, setComments] = useState<Comment[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [newBody, setNewBody] = useState("");
    const [replyTo, setReplyTo] = useState<string | null>(null);
    const [replyBody, setReplyBody] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

    useEffect(() => {
        loadComments();
    }, [pageId]);

    useEffect(() => { loadUsers().then(setUsers).catch(() => {}); }, []);

    const usersById = useMemo(() => {
        const m = new Map<string, User>();
        users.forEach(u => m.set(u.id, u));
        return m;
    }, [users]);

    function authorView(authorId: string) {
        const u = usersById.get(authorId);
        const name = u ? displayName(u) : authorId.slice(0, 8);
        return { name, color: avatarColor(authorId), initial: initials(name) };
    }

    function loadComments() {
        axios.get(`/api/manuals/pages/${pageId}/comments`)
            .then(res => {
                if (res.data.success) setComments(res.data.items);
            })
            .catch(() => {});
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!newBody.trim()) return;
        setSubmitting(true);
        axios.post(`/api/manuals/pages/${pageId}/comments`, {
            targetId: pageId,
            targetType: "page",
            parentCommentId: null,
            body: newBody.trim(),
        })
            .then(res => {
                if (res.data.success) {
                    setNewBody("");
                    loadComments();
                }
            })
            .catch(() => toast.error("Failed to post comment"))
            .finally(() => setSubmitting(false));
    }

    function handleReply(parentId: string) {
        if (!replyBody.trim()) return;
        setSubmitting(true);
        axios.post(`/api/manuals/pages/${pageId}/comments`, {
            targetId: pageId,
            targetType: "page",
            parentCommentId: parentId,
            body: replyBody.trim(),
        })
            .then(res => {
                if (res.data.success) {
                    setReplyTo(null);
                    setReplyBody("");
                    loadComments();
                }
            })
            .catch(() => toast.error("Failed to post reply"))
            .finally(() => setSubmitting(false));
    }

    function handleDelete(commentId: string) {
        axios.delete(`/api/manuals/comments/${commentId}`)
            .then(res => {
                if (res.data.success) loadComments();
            })
            .catch(() => toast.error("Failed to delete"));
    }

    const rootComments = comments.filter(c => !c.parentCommentId);
    const getReplies = (parentId: string) => comments.filter(c => c.parentCommentId === parentId);

    return (
        <div className="m-comments">
            <div className="m-comments-header">
                <MessageSquare size={18} />
                <span>{comments.length} comment{comments.length !== 1 ? "s" : ""}</span>
            </div>

            {rootComments.map(comment => {
                const a = authorView(comment.authorId);
                return (
                <div key={comment.id} className="m-comment">
                    <div className="m-comment-avatar" style={{ background: a.color }}>{a.initial}</div>
                    <div className="m-comment-body">
                        <div className="m-comment-meta">
                            <span className="m-comment-author">{a.name}</span>
                            <span className="m-comment-time">{formatRelative(comment.createdAt)}</span>
                        </div>
                        <div className="m-comment-text">{comment.body}</div>
                        <div className="m-comment-actions">
                            <button onClick={() => { setReplyTo(replyTo === comment.id ? null : comment.id); setReplyBody(""); }}>
                                <Reply size={13} /> Reply
                            </button>
                            {canDelete(comment.authorId) && (
                                <button className="danger" onClick={() => setDeleteTarget(comment.id)}>
                                    <Trash2 size={13} /> Delete
                                </button>
                            )}
                        </div>

                        {/* replies */}
                        {getReplies(comment.id).map(reply => {
                            const ra = authorView(reply.authorId);
                            return (
                            <div key={reply.id} className="m-comment m-comment-reply">
                                <div className="m-comment-avatar sm" style={{ background: ra.color }}>{ra.initial}</div>
                                <div className="m-comment-body">
                                    <div className="m-comment-meta">
                                        <span className="m-comment-author">{ra.name}</span>
                                        <span className="m-comment-time">{formatRelative(reply.createdAt)}</span>
                                    </div>
                                    <div className="m-comment-text">{reply.body}</div>
                                    {canDelete(reply.authorId) && (
                                        <div className="m-comment-actions">
                                            <button className="danger" onClick={() => setDeleteTarget(reply.id)}>
                                                <Trash2 size={13} /> Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            );
                        })}

                        {/* reply form */}
                        {replyTo === comment.id && (
                            <div className="m-comment-reply-form">
                                <textarea
                                    className="m-input"
                                    rows={2}
                                    value={replyBody}
                                    onChange={e => setReplyBody(e.target.value)}
                                    placeholder="Write a reply..."
                                    autoFocus
                                />
                                <div className="m-comment-reply-actions">
                                    <button className="m-btn m-btn-ghost m-btn-sm" onClick={() => setReplyTo(null)}>Cancel</button>
                                    <button
                                        className="m-btn m-btn-primary m-btn-sm"
                                        disabled={submitting || !replyBody.trim()}
                                        onClick={() => handleReply(comment.id)}
                                    >
                                        Reply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                );
            })}

            {/* new comment form */}
            <form className="m-comment-form" onSubmit={handleSubmit}>
                <textarea
                    className="m-input"
                    rows={3}
                    value={newBody}
                    onChange={e => setNewBody(e.target.value)}
                    placeholder="Write a comment..."
                />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <button
                        type="submit"
                        className="m-btn m-btn-primary m-btn-sm"
                        disabled={submitting || !newBody.trim()}
                    >
                        Comment
                    </button>
                </div>
            </form>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete comment"
                    message="Delete this comment? This cannot be undone."
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => { const id = deleteTarget; setDeleteTarget(null); handleDelete(id); }}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}

function formatRelative(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
}
